import { useState, useEffect } from "react";
import { API, useAuth , authFetch, useEditMode } from "@/App";
import { toast } from "sonner";
import { SyncNowButton } from "@/components/SyncNowButton";
import { mutate, confirmDelete } from "@/lib/apiMutate";
import { Package, Layers, Scale, ArrowLeftRight, Search, Plus, MoreHorizontal, Edit, Trash2, Camera, Upload, ImagePlus, X as XIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AuthImage } from "@/components/AuthImage";
import { AuthImageLightbox } from "@/components/AuthImageLightbox";

const BRAND_TABS = [
  { key: "all", label: "All Products" },
  { key: "Lion", label: "Lion Cement" },
  { key: "Hathi", label: "Hathi Cement" },
  { key: "Siddhee", label: "Siddhee Cement" },
  { key: "EFCON", label: "EFCON Blocks" },
  { key: "Cemtite", label: "Cemtite Chemicals" },
];

const BRAND_COLORS = {
  Lion: "bg-[#8B1A1A] text-white",
  Hathi: "bg-amber-600 text-white",
  Siddhee: "bg-blue-600 text-white",
  EFCON: "bg-emerald-600 text-white",
  Cemtite: "bg-purple-600 text-white",
};

const BulkConverter = ({ product }) => {
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("bags");
  const bagsPerMt = product.bags_per_mt || 20;

  const bags = unit === "bags" ? Number(qty) || 0 : (Number(qty) || 0) * bagsPerMt;
  const mt = unit === "mt" ? Number(qty) || 0 : (Number(qty) || 0) / bagsPerMt;

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
        <Scale className="w-3 h-3" /> Bulk Converter (1 Metric Ton = {bagsPerMt} Bags)
      </p>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min="0"
          placeholder="Qty"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="w-20 h-8 text-sm"
          data-testid={`bulk-qty-${product.product_id}`}
        />
        <select
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white"
          data-testid={`bulk-unit-${product.product_id}`}
        >
          <option value="bags">Bags</option>
          <option value="mt">Metric Tons</option>
        </select>
        <ArrowLeftRight className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="text-xs text-slate-600 space-y-0.5">
          <p><span className="font-semibold text-slate-900">{bags.toFixed(bags % 1 === 0 ? 0 : 1)}</span> Bags</p>
          <p><span className="font-semibold text-slate-900">{mt.toFixed(2)}</span> Metric Tons</p>
        </div>
      </div>
    </div>
  );
};

const ProductsPage = () => {
  const { user } = useAuth();
  const canEdit = useEditMode();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBrand, setActiveBrand] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    product_name: "", sku_code: "", brand: "Lion", category: "CEMENT",
    sub_category: "PPC", description: "", base_price: 0, gst_rate: 28,
    unit_of_measurement: "BAG", hsn_code: "", bulk_eligible: false,
    bags_per_mt: 20,
    available_regions: ["ALL"],
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(null);
  const [lightboxPath, setLightboxPath] = useState(null);

  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const fetchProducts = async () => {
    try {
      const res = await authFetch(`${API}/products?limit=50`, {  });
      const data = await res.json();
      if (data.success) setProducts(data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProducts(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingProduct ? `${API}/products/${editingProduct.product_id}` : `${API}/products`;
    const r = await mutate(
      authFetch,
      url,
      {
        method: editingProduct ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, base_price: Number(formData.base_price) }),
      },
      {
        successMessage: editingProduct ? "Product updated" : "Product created",
        failureMessage: "Failed to save product",
      },
    );
    if (!r.ok) return;
    setIsDialogOpen(false);
    setEditingProduct(null);
    resetForm();
    fetchProducts();
  };

  const handleDelete = async (productId) => {
    const r = await confirmDelete(authFetch, `${API}/products/${productId}`, {
      promptMessage: "Delete this product? This cannot be undone.",
      successMessage: "Product deleted",
      failureMessage: "Failed to delete product",
      onSuccess: () => setProducts((prev) => prev.filter((p) => p.product_id !== productId)),
    });
    if (r.ok) fetchProducts();
  };

  const handlePhotoUpload = async (productId, file) => {
    setUploadingPhoto(productId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authFetch(`${API}/products/${productId}/upload-photo`, {
        method: "POST", body: formData,
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Product photo updated");
        fetchProducts();
      } else toast.error(data.detail || "Failed to upload photo");
    } catch { toast.error("Upload failed"); }
    finally { setUploadingPhoto(null); }
  };

  const handleImagesUpload = async (productId, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploadingImages(productId);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await authFetch(`${API}/products/${productId}/upload-images`, {
        method: "POST", body: fd,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Added ${data.meta?.added ?? files.length} image${(data.meta?.added ?? files.length) === 1 ? "" : "s"}`);
        fetchProducts();
      } else toast.error(data.detail || data.error?.message || "Failed to upload images");
    } catch { toast.error("Upload failed"); }
    finally { setUploadingImages(null); }
  };

  const handleImageDelete = async (productId, path) => {
    if (!window.confirm("Remove this image from the gallery?")) return;
    try {
      const res = await authFetch(`${API}/products/${productId}/images?path=${encodeURIComponent(path)}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Image removed");
        fetchProducts();
      } else toast.error(data.detail || data.error?.message || "Failed to remove image");
    } catch { toast.error("Failed to remove image"); }
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      product_name: product.product_name, sku_code: product.sku_code,
      brand: product.brand, category: product.category, sub_category: product.sub_category || "",
      description: product.description || "", base_price: product.base_price / 100,
      gst_rate: product.gst_rate, unit_of_measurement: product.unit_of_measurement,
      hsn_code: product.hsn_code || "", bulk_eligible: product.bulk_eligible || false,
      bags_per_mt: product.bags_per_mt || 20,
      available_regions: product.available_regions && product.available_regions.length > 0 ? product.available_regions : ["ALL"],
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => setFormData({
    product_name: "", sku_code: "", brand: "Lion", category: "CEMENT",
    sub_category: "PPC", description: "", base_price: 0, gst_rate: 28,
    unit_of_measurement: "BAG", hsn_code: "", bulk_eligible: false, bags_per_mt: 20,
    available_regions: ["ALL"],
  });

  const toggleRegion = (code) => {
    setFormData((f) => {
      const arr = Array.isArray(f.available_regions) ? f.available_regions : [];
      if (code === "ALL") return { ...f, available_regions: ["ALL"] };
      const without_all = arr.filter((r) => r !== "ALL");
      const next = without_all.includes(code)
        ? without_all.filter((r) => r !== code)
        : [...without_all, code];
      return { ...f, available_regions: next.length === 0 ? ["ALL"] : next };
    });
  };

  const filteredProducts = products.filter((p) => {
    const matchBrand = activeBrand === "all" || p.brand === activeBrand;
    const matchSearch = !searchQuery || p.product_name.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku_code.toLowerCase().includes(searchQuery.toLowerCase());
    return matchBrand && matchSearch;
  });

  // Group by sub_category within the active brand
  const grouped = {};
  filteredProducts.forEach((p) => {
    const key = p.sub_category || p.category || "Other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(p);
  });

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Product Catalogue</h1>
          <p className="text-slate-500 text-sm mt-1">Avi Cement Limited — Complete Product Range</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64"
              data-testid="product-search"
            />
          </div>
          {isSuperAdmin && (
            <Button onClick={() => { setEditingProduct(null); resetForm(); setIsDialogOpen(true); }}
              className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          )}
          <SyncNowButton entity="products" />
        </div>
      </div>

      {/* Brand Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="brand-tabs">
        {BRAND_TABS.map((tab) => {
          const count = tab.key === "all" ? products.length : products.filter(p => p.brand === tab.key).length;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveBrand(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeBrand === tab.key
                  ? "bg-[#1A1A2E] text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid={`brand-tab-${tab.key}`}
            >
              {tab.label} <span className="text-xs opacity-70 ml-1">({count})</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="w-8 h-8 spinner" /></div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-lg font-medium">No products found</p>
        </div>
      ) : (
        Object.entries(grouped).map(([category, prods]) => (
          <div key={category}>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Layers className="w-4 h-4" /> {category.replace(/_/g, " ")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {prods.map((product) => (
                <Card key={product.product_id} className="p-4 hover:shadow-md transition-shadow" data-testid={`product-card-${product.product_id}`}>
                  {/* Product Photo */}
                  <div className="relative group mb-3">
                    {product.product_photo ? (
                      <AuthImage
                        path={product.product_photo}
                        alt={product.product_name}
                        className="w-full h-32 object-cover rounded-lg bg-slate-100 cursor-pointer"
                        onClick={() => setLightboxPath(product.product_photo)}
                      />
                    ) : (
                      <div className="w-full h-32 bg-slate-50 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-200">
                        <Package className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                    {isSuperAdmin && (
                      <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg cursor-pointer" data-testid={`upload-photo-${product.product_id}`}>
                        {uploadingPhoto === product.product_id ? (
                          <div className="w-6 h-6 spinner" />
                        ) : (
                          <div className="flex items-center gap-1.5 text-white text-xs font-medium">
                            <Camera className="w-4 h-4" /> {product.product_photo ? "Change Photo" : "Add Photo"}
                          </div>
                        )}
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && handlePhotoUpload(product.product_id, e.target.files[0])} />
                      </label>
                    )}
                  </div>

                  {/* Multi-image gallery strip */}
                  {((product.images && product.images.length > 0) || isSuperAdmin) && (
                    <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1" data-testid={`product-gallery-${product.product_id}`}>
                      {(product.images || []).map((img) => (
                        <div key={img} className="relative shrink-0 group/img">
                          <AuthImage
                            path={img}
                            alt=""
                            className="w-12 h-12 object-cover rounded border border-slate-200 cursor-pointer"
                            onClick={() => setLightboxPath(img)}
                          />
                          {isSuperAdmin && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleImageDelete(product.product_id, img); }}
                              className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 text-white text-[10px] flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity shadow"
                              data-testid={`gallery-delete-${product.product_id}-${img.split('/').pop()}`}
                              title="Remove image"
                            >
                              <XIcon className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      ))}
                      {isSuperAdmin && (
                        <label
                          className={`shrink-0 w-12 h-12 border-2 border-dashed border-slate-300 hover:border-[#8B1A1A] rounded flex items-center justify-center cursor-pointer text-slate-400 hover:text-[#8B1A1A] ${uploadingImages === product.product_id ? "animate-pulse" : ""}`}
                          data-testid={`add-gallery-image-${product.product_id}`}
                          title="Add more images"
                        >
                          <ImagePlus className="w-4 h-4" />
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            multiple
                            onChange={(e) => e.target.files && handleImagesUpload(product.product_id, e.target.files)}
                          />
                        </label>
                      )}
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-2">
                    <Badge className={`text-xs ${BRAND_COLORS[product.brand] || "bg-slate-600 text-white"}`}>
                      {product.brand}
                    </Badge>
                    <div className="flex items-center gap-1">
                      {product.bulk_eligible && (
                        <Badge variant="outline" className="text-xs text-blue-700 border-blue-300 bg-blue-50">
                          Bulk
                        </Badge>
                      )}
                      {isSuperAdmin && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => handleDelete(product.product_id)}
                            data-testid={`delete-product-${product.product_id}`}
                            title="Delete product"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-testid={`product-menu-${product.product_id}`}>
                                <MoreHorizontal className="w-3.5 h-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEdit(product)}>
                                <Edit className="w-3.5 h-3.5 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(product.product_id)} className="text-red-600">
                                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </>
                      )}
                    </div>
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900 mt-2">{product.product_name}</h4>
                  <p className="text-xs text-slate-500 mt-1">{product.sku_code} {product.hsn_code ? `· HSN: ${product.hsn_code}` : ""}</p>
                  {product.description && <p className="text-xs text-slate-600 mt-2 line-clamp-2">{product.description}</p>}

                  {/* Region availability badges */}
                  <div className="flex items-center gap-1 mt-2 flex-wrap" data-testid={`product-regions-${product.product_id}`}>
                    {(product.available_regions || ["ALL"]).map((r) => (
                      <Badge
                        key={r}
                        variant="outline"
                        className={`text-[10px] px-1.5 py-0 ${
                          r === "ALL"
                            ? "border-slate-300 text-slate-600 bg-slate-50"
                            : r === "GJ"
                            ? "border-blue-300 text-blue-700 bg-blue-50"
                            : r === "RJ"
                            ? "border-orange-300 text-orange-700 bg-orange-50"
                            : "border-slate-300 text-slate-600 bg-slate-50"
                        }`}
                      >
                        {r === "ALL" ? "🇮🇳 All India" : r === "GJ" ? "GJ Gujarat" : r === "RJ" ? "RJ Rajasthan" : r}
                      </Badge>
                    ))}
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <p className="text-lg font-bold text-slate-900 font-mono">
                        {(product.base_price / 100).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
                        <span className="text-xs text-slate-400 font-normal ml-1">per {product.unit_of_measurement === "METRIC_TON" ? "Metric Ton" : product.unit_of_measurement === "MT" ? "Metric Ton" : product.unit_of_measurement}</span>
                      </p>
                      {(product.unit_of_measurement === "BAG" || product.unit_of_measurement === "MT" || product.unit_of_measurement === "METRIC_TON") && (
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {product.unit_of_measurement === "BAG" 
                            ? `= ${((product.base_price / 100) * (product.bags_per_mt || 20)).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })} / Metric Ton`
                            : `= ${((product.base_price / 100) / (product.bags_per_mt || 20)).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })} / Bag`
                          }
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs">GST {product.gst_rate}%</Badge>
                  </div>

                  {product.bulk_eligible && <BulkConverter product={product} />}
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Product Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Product Name</label>
                <Input value={formData.product_name} onChange={e => setFormData({ ...formData, product_name: e.target.value })} required className="h-8 text-sm" data-testid="product-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">SKU Code</label>
                <Input value={formData.sku_code} onChange={e => setFormData({ ...formData, sku_code: e.target.value })} required className="h-8 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Brand</label>
                <Select value={formData.brand} onValueChange={v => setFormData({ ...formData, brand: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Lion", "Hathi", "Siddhee", "EFCON", "Cemtite"].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Category</label>
                <Select value={formData.category} onValueChange={v => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["CEMENT", "BLOCKS", "CHEMICALS"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Base Price (INR)</label>
                <Input type="number" value={formData.base_price} onChange={e => setFormData({ ...formData, base_price: e.target.value })} className="h-8 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">GST Rate %</label>
                <Input type="number" value={formData.gst_rate} onChange={e => setFormData({ ...formData, gst_rate: Number(e.target.value) })} className="h-8 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">HSN Code</label>
                <Input value={formData.hsn_code} onChange={e => setFormData({ ...formData, hsn_code: e.target.value })} className="h-8 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Unit</label>
                <Select value={formData.unit_of_measurement} onValueChange={v => setFormData({ ...formData, unit_of_measurement: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["BAG", "METRIC_TON", "PIECE", "UNIT"].map(u => <SelectItem key={u} value={u}>{u === "METRIC_TON" ? "Metric Ton" : u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Description</label>
              <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={2} className="text-sm" />
            </div>

            {/* Region availability */}
            <div data-testid="product-region-picker">
              <label className="text-xs font-medium text-slate-600 block mb-1">Region Availability</label>
              <p className="text-[10px] text-slate-400 mb-2">Choose where this product appears for salesmen and dealers</p>
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { code: "ALL", label: "All India", cls: "border-slate-400 bg-slate-50 text-slate-700" },
                  { code: "GJ",  label: "Gujarat",   cls: "border-blue-400 bg-blue-50 text-blue-700" },
                  { code: "RJ",  label: "Rajasthan", cls: "border-orange-400 bg-orange-50 text-orange-700" },
                ].map(({ code, label, cls }) => {
                  const active = (formData.available_regions || []).includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleRegion(code)}
                      data-testid={`region-${code}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                        active ? `${cls} ring-2 ring-offset-1 ring-current/40` : "border-slate-200 bg-white text-slate-400 hover:bg-slate-50"
                      }`}
                    >
                      {active ? "✓ " : "+ "}{label}
                    </button>
                  );
                })}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white">{editingProduct ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {lightboxPath && (
        <AuthImageLightbox path={lightboxPath} onClose={() => setLightboxPath(null)} />
      )}
    </div>
  );
};

export default ProductsPage;
